import { Card, Flex } from '@radix-ui/themes';
import { CommonDialog } from 'components/common/dialogs';
import { ErrorBoundary } from 'components/common/error-boundary';
import { CommonFormGrid } from 'components/common/form/grid';
import { CommonSearchInput } from 'components/common/form/search';
import { CommonTextInput } from 'components/common/form/text';
import { GameDataPlayerSelector } from 'components/common/game-data-player-selector';
import { StatsApiVsManualToggle } from 'components/common/stats-api-vs-manual-toggle';
import { StatsApiManualFormMode } from 'enums/forms';
import { GDPlayerSelectorMode } from 'enums/game-data.enums';
import { IAssignPitcher } from 'interfaces/i-assign-pitcher';
import { IFullDialog } from 'interfaces/i-dialogs';
import { RADIX } from 'lib_ts/enums/radix-ui';
import { clone, first } from 'lodash';
import { useState } from 'react';
import { useAuthStore } from 'stores/use-auth-store';

const COMPONENT_NAME = 'AssignPitcherDialog';

interface IProps {
  defaultValue?: IAssignPitcher;
  onChange: (value: IAssignPitcher) => void;
  onClose: () => void;
}

export const AssignPitcherDialog = (props: IProps) => {
  const current = useAuthStore((s) => s.current);
  const leagueTeamOptions = useAuthStore((s) => s.leagueTeamOptions);

  const [activeTab, setActiveTab] = useState(() => {
    if (!current.mlb_stats_api) {
      // user does not have access to Stats API
      return StatsApiManualFormMode.Manual;
    }

    if (!props.defaultValue) {
      // nothing selected
      return StatsApiManualFormMode.StatsAPI;
    }

    const { mlb, name, team } = props.defaultValue;

    // user has access to Stats API
    const isManual = !mlb && (!!name || !!team);

    if (isManual) {
      // i.e. existing data was input manually
      return StatsApiManualFormMode.Manual;
    }

    return StatsApiManualFormMode.StatsAPI;
  });

  // store changes locally until user saves (i.e. they can close w/o changing anything)
  const [value, setValue] = useState<IAssignPitcher>(
    props.defaultValue ?? {
      mlb: null,
      name: null,
      team: null,
      teamPk: null,
    }
  );

  const mergeProps: IFullDialog = {
    identifier: COMPONENT_NAME,
    width: RADIX.DIALOG.WIDTH.SM,
    title: 'common.assign-pitcher',
    content: (
      <CommonFormGrid columns={1}>
        <StatsApiVsManualToggle
          mode={activeTab}
          onChange={(v) => setActiveTab(v)}
        />

        {activeTab === StatsApiManualFormMode.StatsAPI && (
          <GameDataPlayerSelector
            mode={GDPlayerSelectorMode.Pitcher}
            model={value.mlb}
            onChangeModel={(player) => {
              if (player) {
                setValue({
                  mlb: {
                    playerPk: player.playerPk,
                    sportId: player.sportId,
                    season: player.season,
                  },
                  name: player.name,
                  team: player._mx_team_details?.name ?? null,
                  teamPk: player._mx_team_details?.teamPk ?? null,
                });
                return;
              }

              // reset to nothing
              setValue({
                mlb: null,
                name: null,
                team: null,
                teamPk: null,
              });
            }}
            autoClose
          />
        )}

        {activeTab === StatsApiManualFormMode.Manual && (
          <Card className="no-background" size="1">
            <Flex gap={RADIX.FLEX.GAP.MD} direction="column">
              <CommonTextInput
                id="assign-pitcher-name"
                label="common.name"
                placeholder="common.name"
                value={value.name ?? ''}
                onChange={(v) =>
                  setValue((prev) => {
                    const nextValue = clone(prev);
                    nextValue.name = v || null;
                    nextValue.mlb = null; // changing details via manual entry should clear Stats API selection
                    return nextValue;
                  })
                }
              />

              <CommonSearchInput
                id="assign-pitcher-team"
                name="TeamName"
                label="common.team"
                values={value.team ? [value.team] : []}
                options={leagueTeamOptions}
                onChange={(v) =>
                  setValue((prev) => {
                    const nextValue = clone(prev);
                    nextValue.team = first(v) || null;
                    nextValue.mlb = null; // changing details via manual entry should clear Stats API selection

                    return nextValue;
                  })
                }
                optional
              />
            </Flex>
          </Card>
        )}
      </CommonFormGrid>
    ),
    onClose: props.onClose,
    buttons: [
      {
        label: 'common.save',
        variant: 'solid',
        color: RADIX.COLOR.ACCENT,
        onClick: () => {
          props.onChange(value);
          props.onClose();
        },
      },
    ],
  };

  return (
    <ErrorBoundary identifier={COMPONENT_NAME}>
      <CommonDialog {...mergeProps} />
    </ErrorBoundary>
  );
};
